import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Select,
  Typography,
  Card,
  message,
  Tag,
  Tooltip,
  Avatar,
  Space,
  Divider,
} from "antd";
import {
  UserOutlined,
  LockOutlined,
  TeamOutlined,
  SaveOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import {
  assignRoles,
  getAgentRoles,
} from "../../api/agent_role/agentRoleRequest";
import { getAllRoles } from "../../api/roles/roleRequest";
import { getUsers } from "../../api/users/userRequest";
import PermissionRender from "../../components/PermissionRender";

const { Title, Text } = Typography;
const { Option } = Select;

const AgentRolesPage = () => {
  const [agents, setAgents] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentAgent, setCurrentAgent] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [loadingAgentRoles, setLoadingAgentRoles] = useState(false);

  // Simulate agent data - replace with your actual agent API call
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Mock agents data - replace with your actual API call
        const res = await getUsers();
        setAgents(res);

        // Fetch all roles
        const rolesData = await getAllRoles();
        setRoles(rolesData);
      } catch (error) {
        message.error("Failed to fetch data");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const showRoleModal = async (agent) => {
    setCurrentAgent(agent);
    setLoadingAgentRoles(true);
    setIsModalVisible(true);

    try {
      const agentRoles = await getAgentRoles(agent.id_agent);
      setSelectedRoles(agentRoles.map((role) => role.id));
    } catch (error) {
      message.error("Failed to fetch agent roles");
      console.error(error);
    } finally {
      setLoadingAgentRoles(false);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setCurrentAgent(null);
    setSelectedRoles([]);
  };

  const handleSaveRoles = async () => {
    try {
      setLoading(true);
      await assignRoles(currentAgent.id_agent, selectedRoles);
      message.success(`Roles updated for ${currentAgent.name}`);
      setIsModalVisible(false);
    } catch (error) {
      message.error("Failed to update roles");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Agent",
      key: "agent",
      render: (_, record) => (
        <div className="flex items-center">
          <div>
            <div className="font-medium">{record.full_name}</div>
            <div className="text-sm text-gray-500">{record.email}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Manage Roles",
      key: "actions",
      width: "20%",
      render: (_, record) => (
        <PermissionRender requiredPermission={["agent_role", "update"]}>
          <Button
            type="primary"
            icon={<LockOutlined />}
            onClick={() => showRoleModal(record)}
            className="bg-purple-600 hover:bg-purple-700"
          >
            Assign Roles
          </Button>
        </PermissionRender>
      ),
    },
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <Title level={2} className="m-0">
          <TeamOutlined className="mr-2 text-purple-600" /> Agent Role
          Assignment
        </Title>
        <Text className="text-gray-500">
          Assign roles to agents to control their access permissions
        </Text>
      </div>

      <Card className="shadow-md">
        <Table
          columns={columns}
          dataSource={agents}
          rowKey="id"
          loading={loading}
          className="bg-white rounded-lg"
        />
      </Card>

      <Modal
        title={
          <div className="flex items-center">
            <LockOutlined className="text-lg mr-2 text-purple-600" />
            <span>Manage Roles for {currentAgent?.full_name}</span>
          </div>
        }
        open={isModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            icon={<SaveOutlined />}
            loading={loading}
            onClick={handleSaveRoles}
            className="bg-purple-600 hover:bg-purple-700"
          >
            Save Roles
          </Button>,
        ]}
        width={700}
      >
        {loadingAgentRoles ? (
          <div className="text-center py-8">Loading agent roles...</div>
        ) : (
          <div>
            <div className="mb-4 flex items-start">
              <InfoCircleOutlined className="text-blue-500 mr-2 mt-1" />
              <Text className="text-gray-600">
                Select the roles you want to assign to this agent. Each role
                comes with its own set of permissions.
              </Text>
            </div>

            <div className="border rounded-lg p-4 bg-gray-50 mb-4">
              <div className="flex items-center mb-2">
                <UserOutlined className="text-gray-500 mr-2" />
                <Text strong>{currentAgent?.full_name}</Text>
              </div>
              <div className="text-gray-500 ml-6">{currentAgent?.email}</div>
            </div>

            <div className="mb-4">
              <Text strong>Assign Roles</Text>
              <Select
                mode="multiple"
                placeholder="Select roles"
                value={selectedRoles}
                onChange={setSelectedRoles}
                style={{ width: "100%" }}
                className="mt-2"
                optionLabelProp="label"
              >
                {roles.map((role) => (
                  <Option key={role.id} value={role.id} label={role.name}>
                    <div className="flex items-center">
                      <LockOutlined className="mr-2 text-blue-500" />
                      {role.name}
                    </div>
                  </Option>
                ))}
              </Select>
            </div>

            <Divider />

            <div>
              <Text strong>Selected Roles and Permissions</Text>
              <div className="mt-2 grid grid-cols-1 gap-3">
                {selectedRoles.length > 0 ? (
                  roles
                    .filter((role) => selectedRoles.includes(role.id))
                    .map((role) => (
                      <div
                        key={role.id}
                        className="border rounded-md p-3 bg-white"
                      >
                        <Text strong className="text-blue-600">
                          {role.name}
                        </Text>
                        <div className="mt-2">
                          <Text type="secondary" className="text-xs">
                            Permissions:
                          </Text>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {role.permissions && role.permissions.length > 0 ? (
                              role.permissions.map((perm) => (
                                <Tag
                                  key={perm.id}
                                  color="blue"
                                  className="px-2 py-0.5 text-xs"
                                >
                                  {perm.resource}.{perm.action}
                                </Tag>
                              ))
                            ) : (
                              <Text type="secondary" italic>
                                No permissions
                              </Text>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    No roles selected
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AgentRolesPage;
